import React, { useState, useEffect } from 'react';
import { Container, Card, Form, Button, Alert, Spinner, Table, Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { getAllCompanies } from '../../api/companyApi';
import { schedulingApi } from '../../api/schedulingApi';

const CustomScheduling = () => {
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [roundNumber, setRoundNumber] = useState(1);
  const [visitDate, setVisitDate] = useState('');
  const [excelFile, setExcelFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [conflicts, setConflicts] = useState([]);
  const [showConflictModal, setShowConflictModal] = useState(false);
  const [forceSchedule, setForceSchedule] = useState(false);

  const downloadSampleExcel = () => {
    // Create sample data with 50-minute slots (default interview duration)
    const sampleData = [
      ['Email', 'Time'],
      ['f20210001@pilani.bits-pilani.ac.in', '09:00-09:50'],
      ['f20210002@pilani.bits-pilani.ac.in', '10:00-10:50'],
      ['f20210003@pilani.bits-pilani.ac.in', '11:00-11:50'],
      ['f20210004@pilani.bits-pilani.ac.in', '14:00-14:50'],
      ['f20210005@pilani.bits-pilani.ac.in', '15:00-15:50']
    ];

    // Convert to CSV format
    const csvContent = sampleData.map(row => row.join(',')).join('\n');
    
    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'sample_custom_schedule.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      const companies = await getAllCompanies();
      setCompanies(companies);
    } catch (error) {
      console.error('Error fetching companies:', error);
      toast.error('Failed to fetch companies');
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && (file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || 
                 file.type === 'application/vnd.ms-excel')) {
      setExcelFile(file);
    } else {
      toast.error('Please select a valid Excel file (.xlsx or .xls)');
      e.target.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedCompany || !roundNumber || !visitDate || !excelFile) {
      toast.error('Please fill all required fields and select an Excel file');
      return;
    }

    setLoading(true);
    setConflicts([]);

    try {
      const formData = new FormData();
      formData.append('company_id', selectedCompany);
      formData.append('round_number', roundNumber);
      formData.append('date_str', visitDate);
      formData.append('excel_file', excelFile);
      formData.append('force_schedule', forceSchedule);

      const response = await schedulingApi.customScheduling(formData);
      
      if (response.status === 200) {
        toast.success(response.data.message);
        
        // Show detailed results
        if (response.data.errors && response.data.errors.length > 0) {
          toast.warning(`Scheduled ${response.data.scheduled_students?.length || 0} students. ${response.data.errors.length} errors occurred.`);
          console.log('Scheduling errors:', response.data.errors);
        }
        
        // Reset form
        setSelectedCompany('');
        setRoundNumber(1);
        setVisitDate('');
        setExcelFile(null);
        setForceSchedule(false);
        document.getElementById('excel-file').value = '';
      }
    } catch (error) {
      if (error.response && error.response.status === 409) {
        // Conflicts detected
        setConflicts(error.response.data.conflicts || []);
        setShowConflictModal(true);
      } else {
        console.error('Error in custom scheduling:', error);
        const errorData = error.response?.data;
        const errorMessage = errorData?.message || 'Failed to process custom scheduling';
        
        if (errorData?.errors && Array.isArray(errorData.errors)) {
          // Show validation errors
          toast.error(`Validation failed: ${errorData.errors.slice(0, 3).join(', ')}${errorData.errors.length > 3 ? '...' : ''}`);
        } else {
          toast.error(errorMessage);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForceSchedule = async () => {
    setShowConflictModal(false);
    setForceSchedule(true);
    
    // Resubmit with force schedule
    const formData = new FormData();
    formData.append('company_id', selectedCompany);
    formData.append('round_number', roundNumber);
    formData.append('date_str', visitDate);
    formData.append('excel_file', excelFile);
    formData.append('force_schedule', true);

    setLoading(true);
    try {
      const response = await schedulingApi.customScheduling(formData);
      toast.success(response.data.message);
      
      // Reset form
      setSelectedCompany('');
      setRoundNumber(1);
      setVisitDate('');
      setExcelFile(null);
      setForceSchedule(false);
      document.getElementById('excel-file').value = '';
    } catch (error) {
      console.error('Error in force scheduling:', error);
      const errorMessage = error.response?.data?.message || 'Failed to force schedule';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container className="mt-4">
      <h2 className="mb-4">Custom Scheduling</h2>
      
      <Card>
        <Card.Header>
          <h5>Upload Excel File for Custom Interview Scheduling</h5>
        </Card.Header>
        <Card.Body>
          <Alert variant="info">
            <div className="d-flex justify-content-between align-items-start">
              <div>
                <strong>Excel File Format Requirements:</strong>
                <ul className="mb-0 mt-2">
                  <li>Column 1: <strong>Email</strong> - Valid BITS Pilani email addresses (@pilani.bits-pilani.ac.in)</li>
                  <li>Column 2: <strong>Time</strong> - Time slots in format HH:MM-HH:MM (e.g., 09:00-09:50)</li>
                  <li><strong>Duration:</strong> Must match the company's round interview duration</li>
                  <li><strong>Time Range:</strong> Must be within round operating hours and business hours (06:00-23:00)</li>
                  <li><strong>No Duplicates:</strong> Each email should appear only once</li>
                  <li><strong>Valid Times:</strong> End time must be after start time</li>
                </ul>
              </div>
              <Button variant="outline-primary" size="sm" onClick={downloadSampleExcel}>
                Download Sample
              </Button>
            </div>
          </Alert>

          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Company *</Form.Label>
              <Form.Select
                value={selectedCompany}
                onChange={(e) => setSelectedCompany(e.target.value)}
                required
              >
                <option value="">Select a company...</option>
                {companies.map((company) => (
                  <option key={company.id} value={company.id}>
                    {company.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Round Number *</Form.Label>
              <Form.Control
                type="number"
                min="1"
                value={roundNumber}
                onChange={(e) => setRoundNumber(e.target.value)}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Visit Date *</Form.Label>
              <Form.Control
                type="date"
                value={visitDate}
                onChange={(e) => setVisitDate(e.target.value)}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Excel File *</Form.Label>
              <Form.Control
                id="excel-file"
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileChange}
                required
              />
              <Form.Text className="text-muted">
                Upload an Excel file with student emails and their interview time slots
              </Form.Text>
            </Form.Group>

            <Button 
              type="submit" 
              variant="primary" 
              disabled={loading}
              className="me-2"
            >
              {loading ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Processing...
                </>
              ) : (
                'Upload & Schedule'
              )}
            </Button>

            <Button 
              type="button" 
              variant="secondary" 
              onClick={() => {
                setSelectedCompany('');
                setRoundNumber(1);
                setVisitDate('');
                setExcelFile(null);
                setForceSchedule(false);
                document.getElementById('excel-file').value = '';
              }}
            >
              Reset
            </Button>
          </Form>
        </Card.Body>
      </Card>

      {/* Conflict Modal */}
      <Modal show={showConflictModal} onHide={() => setShowConflictModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Scheduling Conflicts Detected</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="warning">
            <strong>Warning:</strong> The following time slots already have scheduled interviews. 
            Proceeding with force schedule will make existing interviews unscheduled.
          </Alert>
          
          <Table striped bordered hover>
            <thead>
              <tr>
                <th>Student Email</th>
                <th>Time Slot</th>
                <th>Conflicting Student</th>
              </tr>
            </thead>
            <tbody>
              {conflicts.map((conflict, index) => (
                <tr key={index}>
                  <td>{conflict.email}</td>
                  <td>{conflict.time}</td>
                  <td>{conflict.existing_student}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConflictModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleForceSchedule}>
            Force Schedule
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default CustomScheduling;